import AuthService from "@/services/AuthService";
import authFactory from "@/auth/services/authFactory";
import { checkIsTokenExpired } from "@/utils/tokenUtils";
import { getUserDataFromLocalStorage } from "@/utils";

const RELOAD_GUARD_MS = 60_000;

let isReloading = false;

// Same-origin widget iframes in one tab share sessionStorage (the promo center and its nested
// promos, a lobby next to a promo), so each widget keeps its own reload time.
const getReloadKey = () =>
  `chunkReloadAt:${window.location.pathname}:${new URLSearchParams(window.location.search).get("promoId") ?? ""}`;

// A reload authenticates again from the URL. It must not log the user out (a URL token may have
// expired after the host refreshed it by postMessage; a session is only reused through the saved
// token) or log back in a user the host signed out (no saved token).
export const canReloadWithoutLogout = (): boolean => {
  const urlToken = AuthService.getToken();
  if (!urlToken && !authFactory.hasRequiredParams()) return true;
  if (urlToken && checkIsTokenExpired(urlToken)) return false;

  const savedToken = getUserDataFromLocalStorage()?.token;
  return !!savedToken && !checkIsTokenExpired(savedToken);
};

export const reloadPage = () => window.location.reload();

export const reloadOnceAfterChunkError = (reload: () => void = reloadPage): boolean => {
  if (isReloading) return true;
  if (!canReloadWithoutLogout()) return false;

  try {
    const lastReloadAt = Number(sessionStorage.getItem(getReloadKey()));
    if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(getReloadKey(), String(Date.now()));
  } catch {
    return false;
  }

  isReloading = true;
  reload();
  return true;
};
